import { useEffect, useState } from 'react';

import useFetchProjectGroups from '@/micros/choose-template/choose-template/components/choose-template-panel/use-fetch-project-groups';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import { assertReactSelectOptions } from '@/utils/assertions/assert-react-select-options';
import FolderSelect from './controls/folder-select';
import toastTrigger from '@ui/toast/toast-trigger';
import { useAppSelector } from '@/store/hooks';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { getWorkingProjectUuid } from '@/store/slices/project/selectors';
import { AddProjectsToFolder } from '@/connection/projects/add-projects-to-folder';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import { EToastTypes } from '@ui/toast/toast.types';

type AddFolderModalProps = {
  open: boolean;
  handleClose: () => void;
};

const AddFolderModal = ({ open, handleClose }: AddFolderModalProps) => {
  const channel = useAppSelector(getActiveChannel);
  const projectUuid = useAppSelector(getWorkingProjectUuid);

  const [selectedProjectFolders, setSelectedProjectFolders] = useState<{ label: string; value: string } | null>(null);

  const [saving, setSaving] = useState(false);

  const { loading: loadingProjectFolders } = useFetchProjectGroups({ skip: !open });

  const onClose = () => {
    if (saving) return;
    handleClose();
  };

  const handleSave = async () => {
    if (saving || !channel || !projectUuid || !selectedProjectFolders) return;
    try {
      setSaving(true);

      const { value } = selectedProjectFolders;

      const result = await AddProjectsToFolder({
        channel,
        groupUuid: value,
        eventUuids: [projectUuid],
      });

      if (result) {
        toastTrigger('Successfully added project to folder', {
          type: EToastTypes.success,
          autoClose: 5000,
        });
        onClose();
      }
    } catch (error) {
      console.error(error);
      toastTrigger('Failed to add project to folder', {
        type: EToastTypes.error,
        autoClose: 10000,
        description: 'Refresh and try again',
      });
    } finally {
      setSaving(false);
    }
  };

  // reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedProjectFolders(null);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      focusLockProps={{ autoFocus: false }}
      customSize={{
        height: 'auto',
        minHeight: '200px',
        width: '640px',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100vh - 64px)',
      }}
      onRequestClose={onClose}
      RenderHeaderContent={() => {
        return (
          <Container padding="24px 40px">
            <Text tag="h4" className="H4 margin-b-4">
              Add project to folder
            </Text>
          </Container>
        );
      }}
      RenderFooterContent={() => {
        return (
          <Container className="flex fd-row jc-flex-end ai-center gap-8" padding="24px 40px">
            <Button design="LocalBttnSec" onClick={onClose} disabled={saving} className="L1 padding-v-16 padding-h-24">
              Cancel
            </Button>

            <Button
              padding="16px 24px"
              width="auto"
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              disabled={loadingProjectFolders || saving || !selectedProjectFolders}
              onClick={handleSave}
              loading={saving}
              disabledOpacity={0.7}
            >
              <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                Save
              </Text>
            </Button>
          </Container>
        );
      }}
    >
      <Container padding="0 40px 24px 40px" className="flex-column gap-16">
        <FolderSelect
          isOpen={open}
          isDisabled={saving}
          allowMultiSelect={false}
          placeholder="Select a folder"
          label="Project folder"
          onChange={(selected) => {
            try {
              assertReactSelectOptions<{ label: string; value: string; meta?: unknown }>(selected);
              setSelectedProjectFolders(selected.length > 0 ? selected[0] : null);
            } catch (error) {
              console.error(error);
            }
          }}
        />
      </Container>
    </Modal>
  );
};

export default AddFolderModal;
